import React from 'react';
import Layout from '../components/layout/Layout';
import { aboutFull, stats } from '../data/mock';
import { Target, Eye, Award, CheckCircle, Sparkles, Brain, Users, Zap } from 'lucide-react';

const AboutPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-blue-50/50 to-white relative overflow-hidden">
        <div className="absolute inset-0 ai-grid-pattern" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-blue-600">Story</span>
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              {aboutFull.description}
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="p-8 bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl text-center card-hover"
              >
                <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="p-10 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100/50 card-hover">
              <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                {aboutFull.mission}
              </p>
            </div>

            {/* Vision */}
            <div className="p-10 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-100/50 card-hover">
              <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                {aboutFull.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 ai-grid-pattern opacity-10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-600/20 text-blue-400 text-sm font-medium mb-4">
              <Brain className="w-4 h-4 mr-2" />
              AI-Powered
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Our AI Advantage</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Brain, title: 'Smart Analytics', desc: 'AI-powered insights for data-driven decisions' },
              { icon: Zap, title: 'Automated Marketing', desc: 'Intelligent automation for efficient campaigns' },
              { icon: Target, title: 'Predictive Targeting', desc: 'AI algorithms to reach the right audience' },
              { icon: Users, title: '24/7 AI Support', desc: 'Round-the-clock intelligent assistance' },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">What We Stand For</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Innovation', desc: 'Embracing cutting-edge AI technology and creative solutions', icon: Sparkles },
              { title: 'Integrity', desc: 'Transparent processes and ethical business practices', icon: Award },
              { title: 'Excellence', desc: 'Delivering outstanding results that exceed expectations', icon: CheckCircle },
            ].map((value, index) => (
              <div
                key={index}
                className="p-8 bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl text-center card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
